import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "sonner";
import { Button, Table, Modal } from "flowbite-react";

const SuperAdminJobApproval = () => {
  const loggedinUser = useSelector((state) => state.user);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null); // State for viewing job details

  // Fetch pending jobs
  useEffect(() => {
    const fetchPendingJobs = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}job/pending`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setPendingJobs(response.data.data);
      } catch (error) {
        console.error("Error fetching pending jobs:", error.response?.data || error.message);
        toast.error("Failed to load pending jobs");
      } finally {
        setLoading(false);
      }
    };

    if (loggedinUser.currentUser?.role === "superadmin" || localStorage.getItem('userType')) {
      fetchPendingJobs();
    } else {
      toast.error("Unauthorized: Superadmin access required");
    }
  }, [loggedinUser]);

  // Handle approve job
  const handleApproveJob = async (jobId) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}job/jobs/${jobId}/approve`,
        { status: "Online" }, // Match the expected backend logic
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success(response.data.message);
      setPendingJobs(pendingJobs.filter((job) => job._id !== jobId));
    } catch (error) {
      console.error("Error approving job:", error.response?.data || error.message);
      toast.error("Failed to approve job");
    }
  };

  // Handle reject job
  const handleRejectJob = async (jobId) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}job/jobs/${jobId}/reject`,
        { status: "Rejected" }, // Match the expected backend logic
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success(response.data.message);
      setPendingJobs(pendingJobs.filter((job) => job._id !== jobId));
    } catch (error) {
      console.error("Error rejecting job:", error.response?.data || error.message);
      toast.error("Failed to reject job");
    }
  };

  const myloggedinUser = localStorage.getItem('userType');

  if (myloggedinUser !== "superadmin") {
    return <div className="p-6 text-red-600">Access denied: Superadmin only</div>;
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h2 className="text-2xl font-bold mb-4">Job Approval Dashboard</h2>
      {loading ? (
        <div>Loading...</div>
      ) : pendingJobs.length === 0 ? (
        <div>No jobs awaiting approval</div>
      ) : (
        <Table hoverable>
          <Table.Head>
            <Table.HeadCell>Title</Table.HeadCell>
            <Table.HeadCell>Employer</Table.HeadCell>
            <Table.HeadCell>Job Type</Table.HeadCell>
            <Table.HeadCell>Location</Table.HeadCell>
            <Table.HeadCell>Status</Table.HeadCell>
            <Table.HeadCell>Actions</Table.HeadCell>
          </Table.Head>
          <Table.Body>
            {pendingJobs.map((job) => (
              <Table.Row key={job._id} className="bg-white">
                <Table.Cell>{job.jobTitle || "Untitled"}</Table.Cell>
                <Table.Cell>{job.employer?.companyName}</Table.Cell>
                <Table.Cell>{job.jobType}</Table.Cell>
                <Table.Cell>{`${job.city}, ${job.localGovernment}, ${job.state}`}</Table.Cell>
                <Table.Cell>{job.status}</Table.Cell>
                <Table.Cell>
                  <div className="flex gap-2">
                    <Button
                      color="success"
                      onClick={() => handleApproveJob(job._id)}
                    >
                      Approve
                    </Button>
                    <Button
                      color="failure"
                      onClick={() => handleRejectJob(job._id)}
                    >
                      Reject
                    </Button>
                    <Button
                      color="warning"
                      onClick={() => setSelectedJob(job)}
                    >
                      View More
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {/* Modal for View More Details */}
      <Modal show={!!selectedJob} onClose={() => setSelectedJob(null)}>
        <Modal.Header>Job Details</Modal.Header>
        <Modal.Body>
          {selectedJob && (
            <div className="space-y-4">
              <p><strong>Title:</strong> {selectedJob.jobTitle || "Untitled"}</p>
              <p><strong>Employer:</strong> {selectedJob.employer.companyName}</p>
              <p><strong>Job Type:</strong> {selectedJob.jobType}</p>
              <p><strong>Location:</strong> {`${selectedJob.city}, ${selectedJob.localGovernment}, ${selectedJob.state}`}</p>
              <p><strong>Views:</strong> {selectedJob.uniqueViewers}</p>
              <p><strong>Description:</strong> {selectedJob.jobDescription?.join(", ") || "No description"}</p>
              <p><strong>Skills:</strong> {selectedJob.jobSkills?.join(", ") || "No skills"}</p>
              <p><strong>Tags:</strong> {selectedJob.jobTag?.join(", ") || "No tags"}</p>
              <p><strong>Salary:</strong> {selectedJob.salary || "Not specified"}</p>
              <p><strong>Status:</strong> {selectedJob.status}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setSelectedJob(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SuperAdminJobApproval;